import type { EqDataPoint } from './device/commands'
import { API_BASE_URL } from '@/config/api'

export type EqMode = 'game' | 'music' | 'movie'

export interface EqPresetData {
  id: string
  name: string
  datapoint: EqDataPoint[]
  isCustom?: boolean
  createdAt?: number
}

export interface EqListResponse {
  presets: EqPresetData[]
  lastPreset: string
  defaultPreset: string
}

export interface ApiResponse<T = unknown> {
  code: number
  message: string
  data: T
}

const EQ_API_MAP: Record<EqMode, Record<'list' | 'add' | 'remove' | 'pin' | 'find' | 'reset' | 'updatePoint' | 'updateName' | 'setLast', string>> = {
  game: {
    list: '/game/list', add: '/game/addEqData', remove: '/game/deleteEqData',
    pin: '/game/setTop', find: '/game/findEqById', reset: '/game/reset',
    updatePoint: '/game/updatePointByFid', updateName: '/game/updateName', setLast: '/game/setLast',
  },
  music: {
    list: '/music/list', add: '/music/addEqData', remove: '/music/deleteEqData',
    pin: '/music/setTop', find: '/music/findEqById', reset: '/music/reset',
    updatePoint: '/music/updatePointByFid', updateName: '/music/updateName', setLast: '/music/setLast',
  },
  movie: {
    list: '/cinema/list', add: '/cinema/addEqData', remove: '/cinema/deleteEqData',
    pin: '/cinema/setTop', find: '/cinema/findEqById', reset: '/cinema/reset',
    updatePoint: '/cinema/updatePointByFid', updateName: '/cinema/updateName', setLast: '/cinema/setLast',
  },
}

function createEqFormData(uuid: string, linktype: string, fields: Record<string, string> = {}): FormData {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('linktype', linktype)
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
  return formData
}

async function postEq<T>(mode: EqMode, action: keyof typeof EQ_API_MAP.game, formData: FormData): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode][action]}`, {
    method: 'POST',
    body: formData,
    // 写操作在刷新/关闭页面时也尽量完成，避免“UI 已提交但网络请求被导航中断”。
    // list/find 只是读取，不需要 keepalive。
    keepalive: action !== 'list' && action !== 'find',
  })
  return response.json()
}

export function getEqList(mode: EqMode, uuid: string, linktype = '1'): Promise<ApiResponse<EqListResponse>> {
  return postEq(mode, 'list', createEqFormData(uuid, linktype))
}

export function addEqPreset(
  mode: EqMode,
  uuid: string,
  preset: EqPresetData,
  linktype = '1',
): Promise<ApiResponse<{ id: string }>> {
  const config = {
    id: String(preset.id),
    name: String(preset.name),
    datapoint: preset.datapoint.map((point, index) => ({
      fid: point.fid || index + 1,
      freq: String(point.freq).replace(/hz$/i, 'HZ'),
      gain: String(point.gain),
      q: String(point.q || '0.75'),
    })),
  }
  return postEq(mode, 'add', createEqFormData(uuid, linktype, { config: JSON.stringify(config) }))
}

export function removeEqPreset(mode: EqMode, uuid: string, presetId: string, linktype = '1'): Promise<ApiResponse> {
  return postEq(mode, 'remove', createEqFormData(uuid, linktype, { presetId }))
}

export function pinEqPreset(mode: EqMode, uuid: string, presetId: string, linktype = '1'): Promise<ApiResponse> {
  return postEq(mode, 'pin', createEqFormData(uuid, linktype, { presetId }))
}

export function findEqById(mode: EqMode, uuid: string, presetId: string, linktype = '1'): Promise<ApiResponse<EqPresetData>> {
  return postEq(mode, 'find', createEqFormData(uuid, linktype, { presetId }))
}

export function resetEqPreset(mode: EqMode, uuid: string, presetId: string, linktype = '1'): Promise<ApiResponse> {
  return postEq(mode, 'reset', createEqFormData(uuid, linktype, { presetId }))
}

export function updateEqPoint(
  mode: EqMode,
  uuid: string,
  presetId: string,
  point: EqDataPoint,
  linktype = '1',
): Promise<ApiResponse> {
  return postEq(mode, 'updatePoint', createEqFormData(uuid, linktype, {
    presetId,
    fid: String(point.fid),
    newFreq: String(point.freq).replace(/hz$/i, 'hz'),
    newGain: String(point.gain),
  }))
}

export function setLastEqPreset(
  mode: EqMode,
  uuid: string,
  presetId: string,
  linktype = '1',
): Promise<ApiResponse<EqListResponse>> {
  return postEq(mode, 'setLast', createEqFormData(uuid, linktype, { presetId }))
}

export function renameEqPreset(
  mode: EqMode,
  uuid: string,
  presetId: string,
  newName: string,
  linktype = '1',
): Promise<ApiResponse<EqListResponse>> {
  return postEq(mode, 'updateName', createEqFormData(uuid, linktype, { presetId, newName }))
}
